import React, { useState, useEffect } from 'react';
import SlideoutPanel from '../components/Common/SlideoutPanel';
import { useSlideout } from '../hooks/useSlideout';
import PageHeader from '../components/Common/PageHeader';
import { TableSkeleton, StatsSkeleton } from '../components/Common/SkeletonLoader';
import apiService from '../services/api';

const CustomersView = ({ showNotification }) => {
  // Use shared slideout hook
  const { isOpen, title, content, width, openSlideout, closeSlideout } = useSlideout();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Customers data from API
  const [customers, setCustomers] = useState([]);

  // Fetch customers from API
  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setApiError(false);
      const response = await apiService.getCustomers();
      const transformedCustomers = response.map(transformApiCustomer);
      setCustomers(transformedCustomers);
    } catch (error) {
      setCustomers([]);
      setApiError(true);
    } finally {
      setLoading(false);
    }
  };

  // Transform API customer data to match frontend structure
  const transformApiCustomer = (apiCustomer) => ({
    id: apiCustomer.id,
    customerNumber: apiCustomer.customerNumber,
    name: `${apiCustomer.firstName || ''} ${apiCustomer.lastName || ''}`.trim(),
    email: apiCustomer.email || '',
    phone: apiCustomer.phone || apiCustomer.mobile || '',
    dateOfBirth: apiCustomer.dateOfBirth ? new Date(apiCustomer.dateOfBirth).toISOString().split('T')[0] : '',
    address: {
      street: apiCustomer.addressLine1 || '',
      city: apiCustomer.city || '',
      postcode: apiCustomer.postalCode || ''
    },
    emergencyContact: {
      name: apiCustomer.emergencyContactName || '',
      phone: apiCustomer.emergencyContactPhone || '',
      relationship: apiCustomer.emergencyContactRelationship || ''
    },
    preferences: {
      practitioner: '',
      location: '',
      communicationMethod: apiCustomer.preferredCommunication || 'email'
    },
    medicalHistory: {
      conditions: [],
      allergies: [],
      medications: []
    },
    status: apiCustomer.status === 0 ? 'Active' : 'Inactive',
    totalBookings: apiCustomer.totalBookings || 0,
    totalSpent: apiCustomer.totalSpent || 0,
    lastVisit: apiCustomer.lastAppointmentDate ? new Date(apiCustomer.lastAppointmentDate).toISOString().split('T')[0] : '',
    joinDate: apiCustomer.registrationDate ? new Date(apiCustomer.registrationDate).toISOString().split('T')[0] : '',
    notes: apiCustomer.notes || ''
  });

  // Transform frontend customer data to API format
  const transformCustomerToApi = (customerData) => {
    const [firstName = '', lastName = ''] = customerData.name.split(' ');
    return {
      firstName,
      lastName,
      email: customerData.email,
      phone: customerData.phone,
      mobile: customerData.phone,
      dateOfBirth: customerData.dateOfBirth ? new Date(customerData.dateOfBirth).toISOString() : null,
      addressLine1: customerData.address?.street || '',
      city: customerData.address?.city || '',
      postalCode: customerData.address?.postcode || '',
      country: 'United Kingdom',
      emergencyContactName: customerData.emergencyContact?.name || '',
      emergencyContactPhone: customerData.emergencyContact?.phone || '',
      emergencyContactRelationship: customerData.emergencyContact?.relationship || '',
      preferredCommunication: customerData.preferences?.communicationMethod || 'email',
      marketingConsent: false,
      notes: customerData.notes || '',
      status: customerData.status === 'Active' ? 0 : 1
    };
  };


  // Filter and sort customers
  const getFilteredCustomers = () => {
    let filtered = customers.filter(customer => {
      const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           customer.phone.includes(searchTerm);
      
      const matchesStatus = statusFilter === '' || customer.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });

    // Sort customers
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'lastVisit':
          return new Date(b.lastVisit) - new Date(a.lastVisit);
        case 'totalSpent':
          return b.totalSpent - a.totalSpent;
        case 'totalBookings':
          return b.totalBookings - a.totalBookings;
        default:
          return 0;
      }
    });

    return filtered;
  };

  const handleAddCustomer = () => {
    const formContent = (
      <AddCustomerForm
        onSubmit={handleCreateCustomer}
        onCancel={closeSlideout}
        submitting={submitting}
      />
    );

    openSlideout('Add New Customer', formContent, '750px');
  };

  const handleCreateCustomer = async (customerData) => {
    if (submitting) return;

    try {
      setSubmitting(true);

      // Transform frontend customer data to API format
      const apiData = transformCustomerToApi(customerData);
      const newCustomer = await apiService.createCustomer(apiData);

      // Add to local state
      const transformedCustomer = transformApiCustomer(newCustomer);
      setCustomers(prev => [...prev, transformedCustomer]);

      showNotification('Customer created successfully', 'success');
      closeSlideout();
    } catch (error) {
      console.error('Error creating customer:', error);
      showNotification('Failed to create customer', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewCustomer = (customer) => {
    const detailContent = <CustomerDetail customer={customer} onEdit={() => handleEditCustomer(customer)} />;
    openSlideout(`Customer: ${customer.name}`, detailContent, '600px');
  };

  const handleEditCustomer = (customer) => {
    const formContent = (
      <AddCustomerForm
        customer={customer}
        onSubmit={(data) => handleUpdateCustomer(customer.id, data)}
        onCancel={closeSlideout}
        submitting={submitting}
      />
    );
    
    openSlideout('Edit Customer', formContent, '750px');
  };

  const handleUpdateCustomer = async (customerId, customerData) => {
    if (submitting) return;

    try {
      setSubmitting(true);

      // Find the customer to get the actual API ID
      const customer = customers.find(c => c.id === customerId);
      if (!customer) {
        throw new Error('Customer not found');
      }

      // Transform frontend customer data to API format
      const apiData = transformCustomerToApi(customerData);
      const updatedCustomer = await apiService.updateCustomer(customer.id, apiData);

      // Update local state
      const transformedCustomer = transformApiCustomer(updatedCustomer);
      setCustomers(prev => prev.map(c =>
        c.id === customerId ? transformedCustomer : c
      ));

      showNotification('Customer updated successfully', 'success');
      closeSlideout();
    } catch (error) {
      console.error('Error updating customer:', error);
      showNotification('Failed to update customer', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await apiService.deleteCustomer(customerId);
        setCustomers(prev => prev.filter(customer => customer.id !== customerId));
        showNotification('Customer deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting customer:', error);
        showNotification('Failed to delete customer', 'error');
      }
    }
  };

  const filteredCustomers = getFilteredCustomers();

  return (
    <div className="customers-view">
      <PageHeader pageKey="customers">
        <button
          className="btn btn-secondary"
          onClick={() => showNotification('Customer export started', 'info')}
        >
          <span className="icon icon--export icon--default"></span>
          Export
        </button>
        <button
          className="btn btn-primary"
          onClick={handleAddCustomer}
        >
          <span className="icon icon--add icon--success"></span>
          Add Customer
        </button>
      </PageHeader>

      {/* Stats Cards */}
      {loading ? (
        <StatsSkeleton count={3} />
      ) : (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-content">
              <div className="stat-number">{customers.length}</div>
              <div className="stat-label">Total Customers</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-content">
              <div className="stat-number">{customers.filter(c => c.status === 'Active').length}</div>
              <div className="stat-label">Active Customers</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-content">
              <div className="stat-number">£{customers.reduce((sum, c) => sum + (c.totalSpent || 0), 0).toLocaleString()}</div>
              <div className="stat-label">Total Revenue</div>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Customer Database</h3>
          <div className="header-controls">
            <div className="view-toggle">
              <button 
                className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
              >
                📋 Table
              </button>
              <button 
                className={`view-btn ${viewMode === 'cards' ? 'active' : ''}`}
                onClick={() => setViewMode('cards')}
              >
                📇 Cards
              </button>
            </div>
          </div>
        </div>
        
        <div className="card-content">
          {/* Filters */}
          <div className="filters-bar">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            
            <select 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>

            <select 
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="name">Sort by Name</option>
              <option value="lastVisit">Sort by Last Visit</option>
              <option value="totalSpent">Sort by Total Spent</option>
              <option value="totalBookings">Sort by Bookings</option>
            </select>

            <div className="results-count">
              {filteredCustomers.length} of {customers.length} customers
            </div>
          </div>

          {/* Customer List */}
          {loading ? (
            <TableSkeleton rows={7} columns={7} hasAvatar={true} />
          ) : apiError ? (
            <div className="api-error-state">
              <div className="api-error-state__icon">⚠️</div>
              <h3>Could not load customers</h3>
              <p>There was a problem connecting to the server. Please check your connection and try again.</p>
              <button className="btn btn-primary" onClick={fetchCustomers}>Retry</button>
            </div>
          ) : viewMode === 'table' ? (
            <div className="material-table-container">
              <div className="material-table-header">
                <h3 className="material-table-title">Customer Overview</h3>
                <span className="material-table-meta">Showing {filteredCustomers.length} of {customers.length} customers</span>
              </div>

              <table className="material-table">
                <thead>
                  <tr>
                    <th className="material-th">Customer</th>
                    <th className="material-th">Contact</th>
                    <th className="material-th">Last Visit</th>
                    <th className="material-th">Bookings</th>
                    <th className="material-th">Total Spent</th>
                    <th className="material-th">Status</th>
                    <th className="material-th">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map(customer => (
                    <tr key={customer.id} className="material-tr">
                      <td className="material-cell">
                        <div className="material-customer-cell">
                          <div className="material-customer-avatar">{customer.name.charAt(0)}</div>
                          <div className="material-customer-info">
                            <div className="material-body-large">{customer.name}</div>
                            <div className="material-body-small material-text-secondary">{customer.customerNumber}</div>
                          </div>
                        </div>
                      </td>
                      <td className="material-cell">
                        <div className="material-contact-info">
                          <div className="material-contact-primary">{customer.email}</div>
                          <div className="material-contact-secondary">{customer.phone}</div>
                        </div>
                      </td>
                      <td className="material-cell">
                        <span className="material-body-medium">{customer.lastVisit ? new Date(customer.lastVisit).toLocaleDateString() : 'Never'}</span>
                      </td>
                      <td className="material-cell">
                        <span className="material-body-medium">{customer.totalBookings}</span>
                      </td>
                      <td className="material-cell">
                        <span className="material-body-medium">£{(customer.totalSpent || 0).toLocaleString()}</span>
                      </td>
                      <td className="material-cell">
                        <span className={`material-chip--${customer.status === 'Active' ? 'success' : 'error'}`}>
                          {customer.status}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <div className="action-icons">
                          <button
                            className="action-icon action-icon--view"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewCustomer(customer);
                            }}
                            data-tooltip="View Details"
                          ></button>
                          <button
                            className="action-icon action-icon--edit"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditCustomer(customer);
                            }}
                            data-tooltip="Edit Customer"
                          ></button>
                          <button
                            className="action-icon action-icon--delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCustomer(customer.id);
                            }}
                            data-tooltip="Delete Customer"
                          ></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="customers-cards">
              {filteredCustomers.map(customer => (
                <div key={customer.id} className="customer-card" onClick={() => handleViewCustomer(customer)}>
                  <div className="card-header">
                    <div className="customer-avatar">{customer.name.charAt(0)}</div>
                    <div className="customer-basic">
                      <h4>{customer.name}</h4>
                      <p>{customer.customerNumber}</p>
                    </div>
                    <span className={`status-badge ${customer.status.toLowerCase()}`}>
                      {customer.status}
                    </span>
                  </div>
                  <div className="card-body">
                    <div className="contact-row">
                      <span>📧 {customer.email}</span>
                    </div>
                    <div className="contact-row">
                      <span>📞 {customer.phone}</span>
                    </div>
                    <div className="stats-row">
                      <span>{customer.totalBookings} bookings</span>
                      <span>£{customer.totalSpent} spent</span>
                    </div>
                    {customer.lastVisit && (
                      <div className="last-visit">
                        Last visit: {new Date(customer.lastVisit).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && !apiError && filteredCustomers.length === 0 && (
            <div className="empty-state">
              <div className="empty-state-icon">👥</div>
              <h3>{searchTerm || statusFilter ? 'No matches found' : 'No customers yet'}</h3>
              <p>{searchTerm || statusFilter ? 'Try adjusting your search or filter criteria.' : 'Add your first customer to start building your patient database.'}</p>
              {!searchTerm && !statusFilter && (
                <button className="btn btn-primary" onClick={handleAddCustomer}>
                  Add First Customer
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <SlideoutPanel
        isOpen={isOpen}
        onClose={closeSlideout}
        title={title}
        width={width}
      >
        {content}
      </SlideoutPanel>
    </div>
  );
};

// Add Customer Form Component
const AddCustomerForm = ({ customer, onSubmit, onCancel, submitting }) => {
  const [formData, setFormData] = useState({
    name: customer?.name || '',
    email: customer?.email || '',
    phone: customer?.phone || '',
    dateOfBirth: customer?.dateOfBirth || '',
    address: {
      street: customer?.address?.street || '',
      city: customer?.address?.city || '',
      postcode: customer?.address?.postcode || ''
    },
    emergencyContact: {
      name: customer?.emergencyContact?.name || '',
      phone: customer?.emergencyContact?.phone || '',
      relationship: customer?.emergencyContact?.relationship || ''
    },
    preferences: {
      practitioner: customer?.preferences?.practitioner || '',
      location: customer?.preferences?.location || '',
      communicationMethod: customer?.preferences?.communicationMethod || 'Email'
    },
    medicalHistory: {
      conditions: customer?.medicalHistory?.conditions?.join(', ') || '',
      allergies: customer?.medicalHistory?.allergies?.join(', ') || '',
      medications: customer?.medicalHistory?.medications?.join(', ') || ''
    },
    notes: customer?.notes || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Process medical history arrays
    const processedData = {
      ...formData,
      medicalHistory: {
        conditions: formData.medicalHistory.conditions.split(',').map(s => s.trim()).filter(s => s),
        allergies: formData.medicalHistory.allergies.split(',').map(s => s.trim()).filter(s => s),
        medications: formData.medicalHistory.medications.split(',').map(s => s.trim()).filter(s => s)
      }
    };
    
    onSubmit(processedData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [section, field] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="add-customer-form">
      <div className="form-section">
        <h4>Personal Information</h4>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Full Name *</label>
            <input
              type="text"
              name="name"
              className="form-input"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Date of Birth</label>
            <input
              type="date"
              name="dateOfBirth"
              className="form-input"
              value={formData.dateOfBirth}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Email *</label>
            <input
              type="email"
              name="email"
              className="form-input"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input
              type="tel"
              name="phone"
              className="form-input"
              value={formData.phone}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Address</h4>
        <div className="form-grid">
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label className="form-label">Street Address</label>
            <input
              type="text"
              name="address.street"
              className="form-input"
              value={formData.address.street}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label className="form-label">City</label>
            <input
              type="text"
              name="address.city"
              className="form-input"
              value={formData.address.city}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Postcode</label>
            <input
              type="text"
              name="address.postcode"
              className="form-input"
              value={formData.address.postcode}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Emergency Contact</h4>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Name</label>
            <input
              type="text"
              name="emergencyContact.name"
              className="form-input"
              value={formData.emergencyContact.name}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input
              type="tel"
              name="emergencyContact.phone"
              className="form-input"
              value={formData.emergencyContact.phone}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Relationship</label>
            <select
              name="emergencyContact.relationship"
              className="form-input"
              value={formData.emergencyContact.relationship}
              onChange={handleChange}
            >
              <option value="">Select relationship</option>
              <option value="Spouse">Spouse</option>
              <option value="Partner">Partner</option>
              <option value="Parent">Parent</option>
              <option value="Child">Child</option>
              <option value="Sibling">Sibling</option>
              <option value="Friend">Friend</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Preferences</h4>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Preferred Practitioner</label>
            <select
              name="preferences.practitioner"
              className="form-input"
              value={formData.preferences.practitioner}
              onChange={handleChange}
            >
              <option value="">No preference</option>
              <option value="Dr. Lisa Chen">Dr. Lisa Chen</option>
              <option value="Dr. John Smith">Dr. John Smith</option>
              <option value="Dr. Priya Patel">Dr. Priya Patel</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Preferred Location</label>
            <select
              name="preferences.location"
              className="form-input"
              value={formData.preferences.location}
              onChange={handleChange}
            >
              <option value="">No preference</option>
              <option value="Harley Street">Harley Street</option>
              <option value="Canary Wharf">Canary Wharf</option>
              <option value="Kensington">Kensington</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Communication Method</label>
            <select
              name="preferences.communicationMethod"
              className="form-input"
              value={formData.preferences.communicationMethod}
              onChange={handleChange}
            >
              <option value="Email">Email</option>
              <option value="SMS">SMS</option>
              <option value="Phone">Phone</option>
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Medical History</h4>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Medical Conditions</label>
            <input
              type="text"
              name="medicalHistory.conditions"
              className="form-input"
              value={formData.medicalHistory.conditions}
              onChange={handleChange}
              placeholder="Separate with commas"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Allergies</label>
            <input
              type="text"
              name="medicalHistory.allergies"
              className="form-input"
              value={formData.medicalHistory.allergies}
              onChange={handleChange}
              placeholder="Separate with commas"
            />
          </div>
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label className="form-label">Current Medications</label>
            <input
              type="text"
              name="medicalHistory.medications"
              className="form-input"
              value={formData.medicalHistory.medications}
              onChange={handleChange}
              placeholder="Separate with commas"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <div className="form-group">
          <label className="form-label">Notes</label>
          <textarea
            name="notes"
            className="form-input"
            value={formData.notes}
            onChange={handleChange}
            rows="3"
            placeholder="Additional notes about the customer..."
          />
        </div>
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Saving...' : (customer ? 'Update Customer' : 'Add Customer')}
        </button>
      </div>
    </form>
  );
};

// Customer Detail Component
const CustomerDetail = ({ customer, onEdit }) => {
  return (
    <div className="customer-detail">
      <div className="detail-header">
        <div className="customer-avatar large">{customer.name.charAt(0)}</div>
        <div className="customer-info">
          <h2>{customer.name}</h2>
          <p className="customer-id">{customer.customerNumber}</p>
          <span className={`status-badge ${customer.status.toLowerCase()}`}>
            {customer.status}
          </span>
        </div>
        <button className="btn btn-primary" onClick={onEdit}>
          Edit Customer
        </button>
      </div>

      <div className="detail-sections">
        <div className="detail-section">
          <h3>Contact Information</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Email</label>
              <span>{customer.email}</span>
            </div>
            <div className="detail-item">
              <label>Phone</label>
              <span>{customer.phone}</span>
            </div>
            <div className="detail-item">
              <label>Date of Birth</label>
              <span>{new Date(customer.dateOfBirth).toLocaleDateString()}</span>
            </div>
            <div className="detail-item">
              <label>Address</label>
              <span>{customer.address.street}, {customer.address.city} {customer.address.postcode}</span>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <h3>Emergency Contact</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Name</label>
              <span>{customer.emergencyContact.name}</span>
            </div>
            <div className="detail-item">
              <label>Phone</label>
              <span>{customer.emergencyContact.phone}</span>
            </div>
            <div className="detail-item">
              <label>Relationship</label>
              <span>{customer.emergencyContact.relationship}</span>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <h3>Preferences</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Preferred Practitioner</label>
              <span>{customer.preferences.practitioner}</span>
            </div>
            <div className="detail-item">
              <label>Preferred Location</label>
              <span>{customer.preferences.location}</span>
            </div>
            <div className="detail-item">
              <label>Communication Method</label>
              <span>{customer.preferences.communicationMethod}</span>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <h3>Medical History</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Conditions</label>
              <span>{customer.medicalHistory.conditions.join(', ') || 'None'}</span>
            </div>
            <div className="detail-item">
              <label>Allergies</label>
              <span>{customer.medicalHistory.allergies.join(', ') || 'None'}</span>
            </div>
            <div className="detail-item">
              <label>Medications</label>
              <span>{customer.medicalHistory.medications.join(', ') || 'None'}</span>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <h3>Booking Summary</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Total Bookings</label>
              <span>{customer.totalBookings}</span>
            </div>
            <div className="detail-item">
              <label>Total Spent</label>
              <span>£{customer.totalSpent}</span>
            </div>
            <div className="detail-item">
              <label>Last Visit</label>
              <span>{customer.lastVisit ? new Date(customer.lastVisit).toLocaleDateString() : 'Never'}</span>
            </div>
            <div className="detail-item">
              <label>Customer Since</label>
              <span>{new Date(customer.joinDate).toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        {customer.notes && (
          <div className="detail-section">
            <h3>Notes</h3>
            <p className="notes-text">{customer.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomersView;